import { useState, useEffect } from 'react';
import { fetchUserProfile } from '../api/api';
import { useAuth } from '../context/AuthContext';

// The signed-in customer's default delivery pincode, or null.
//
// Several screens need this for the same reason: the backend's delivery-area
// model is a per-store pincode allowlist, so "can this store actually deliver
// to me" is answered by comparing the customer's default address zipCode
// against each store's deliveryPincodes. The home feed and search pass it to
// the API so undeliverable stores are filtered server-side; the Stores page
// uses it to badge each card.
//
// Returns null for a logged-out visitor or a customer with no saved address -
// which callers must treat as "unknown", not "delivers nowhere". Every
// consumer deliberately falls back to showing all stores in that case, since
// an empty home page is a worse first impression than one listing a store that
// turns out not to deliver.
const useDefaultZip = () => {
  const { user } = useAuth();
  const [defaultZip, setDefaultZip] = useState(null);

  useEffect(() => {
    if (!user) {
      setDefaultZip(null);
      return;
    }

    let cancelled = false;

    fetchUserProfile(user.uid)
      .then((profile) => {
        if (cancelled) return;
        const addresses = profile.addresses || [];
        const defaultAddress = addresses.find((a) => a.isDefault) || addresses[0];
        setDefaultZip(defaultAddress?.zipCode || null);
      })
      .catch(() => {
        if (!cancelled) setDefaultZip(null);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  return defaultZip;
};

export default useDefaultZip;
